import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import Footer from "../components/cargo/Footer";
import IdentifyStep from "../components/deleteAccount/IdentifyStep";
import OtpStep from "../components/deleteAccount/OtpStep";
import ConfirmStep, { Outcome } from "../components/deleteAccount/ConfirmStep";
import ResultStep from "../components/deleteAccount/ResultStep";
import { Identifier, VerifyResult } from "../api/accountDeletion";

// Public account-deletion page linked from the App Store / Google Play listings
type Step =
    | { name: "identify" }
    | { name: "otp"; identifier: Identifier; display: string }
    | { name: "confirm"; verified: VerifyResult }
    | { name: "result"; outcome: Outcome };

export default function DeleteAccount() {
    const { t, i18n } = useTranslation();
    const [step, setStep] = useState<Step>({ name: "identify" });
    const isArabic = i18n.language === "ar";

    useEffect(() => {
        document.title = t("delete_account.page_title");
    }, [t, i18n.language]);

    useEffect(() => {
        window.scrollTo({ top: 0 });
    }, [step.name]);

    const formatDate = (iso: string) =>
        new Date(iso).toLocaleDateString(isArabic ? "ar-AE" : "en-GB", {
            day: "numeric",
            month: "long",
            year: "numeric",
        });

    const restart = () => setStep({ name: "identify" });

    return (
        <div dir={isArabic ? "rtl" : "ltr"} className="flex min-h-screen flex-col bg-gray-50">
            <header className="bg-[#2C2F33]">
                <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-4 md:px-8">
                    <Link to="/">
                        <img src="/cargo/herosection/cargo_logo.svg" alt="CARGO Logo" className="h-10 md:h-12" />
                    </Link>
                    <button
                        type="button"
                        onClick={() => i18n.changeLanguage(isArabic ? "en" : "ar")}
                        className="rounded-lg border border-white/30 px-4 py-2 text-sm font-semibold text-white transition hover:border-white"
                    >
                        {isArabic ? "English" : "العربية"}
                    </button>
                </div>
            </header>

            <main className="mx-auto w-full max-w-2xl flex-1 px-4 py-10 md:py-16">
                <h1 className="text-3xl font-bold text-gray-900 md:text-4xl">{t("delete_account.title")}</h1>

                {step.name === "identify" && (
                    <section className="mt-6 space-y-6 text-gray-700">
                        <p>{t("delete_account.intro")}</p>
                        <div className="grid gap-4 md:grid-cols-2">
                            <div className="rounded-2xl border border-gray-200 bg-white p-5">
                                <h2 className="mb-3 font-bold text-gray-900">{t("delete_account.deleted_title")}</h2>
                                <ul className="list-disc space-y-2 ps-5 text-sm">
                                    {[1, 2, 3, 4].map((n) => (
                                        <li key={n}>{t(`delete_account.deleted_${n}`)}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="rounded-2xl border border-gray-200 bg-white p-5">
                                <h2 className="mb-3 font-bold text-gray-900">{t("delete_account.kept_title")}</h2>
                                <ul className="list-disc space-y-2 ps-5 text-sm">
                                    {[1, 2, 3].map((n) => (
                                        <li key={n}>{t(`delete_account.kept_${n}`)}</li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                        <div className="rounded-2xl border border-[#00B9EC]/30 bg-[#00B9EC]/5 p-5 text-sm">
                            <h2 className="mb-2 font-bold text-gray-900">{t("delete_account.timing_title")}</h2>
                            <p>{t("delete_account.timing_body")}</p>
                            <p className="mt-2">{t("delete_account.active_orders_note")}</p>
                        </div>
                    </section>
                )}

                <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm md:p-8">
                    {step.name === "identify" && (
                        <IdentifyStep onSent={(identifier, display) => setStep({ name: "otp", identifier, display })} />
                    )}
                    {step.name === "otp" && (
                        <OtpStep
                            identifier={step.identifier}
                            display={step.display}
                            onVerified={(verified) => setStep({ name: "confirm", verified })}
                            onBack={restart}
                        />
                    )}
                    {step.name === "confirm" && (
                        <ConfirmStep
                            token={step.verified.token}
                            pendingDeletion={step.verified.pendingDeletion}
                            scheduledFor={step.verified.scheduledFor}
                            formatDate={formatDate}
                            onDone={(outcome) => setStep({ name: "result", outcome })}
                            onRestart={restart}
                        />
                    )}
                    {step.name === "result" && <ResultStep outcome={step.outcome} formatDate={formatDate} />}
                </div>
            </main>

            <Footer />
        </div>
    );
}
